import React, { useEffect, useMemo, useState } from 'react';
import { CalendarCheck, Loader2, MapPin, RefreshCw, Repeat, User as UserIcon, Video } from 'lucide-react';
import { fetchPatientSessions, type PatientSession } from '../services/patientsApi';
import { describeError } from '../utils/describeError';
import { FREQUENCY_STATUS, filterSessions, sessionStatus, summarize, todayIso, type FrequencyFilter } from '../utils/frequency';

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
const fmtDate = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : '—');
const fmtMoney = (v: string | null) => {
  if (!v) return null;
  const n = Number.parseFloat(String(v).replace(/[^\d,.-]/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : null;
};
const isOnline = (m: string | null) => (m || '').toUpperCase().includes('ONLINE');

/**
 * Bloco #75: subaba "Frequência" da ficha do paciente — sessões passadas e
 * futuras vindas da agenda (interna, link externo e recorrências), só leitura.
 */
export const PatientFrequency: React.FC<{ patientId: string }> = ({ patientId }) => {
  const [sessions, setSessions] = useState<PatientSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<FrequencyFilter>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const today = todayIso();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setSessions(await fetchPatientSessions(patientId));
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, [patientId]);

  const resumo = useMemo(() => summarize(sessions, today), [sessions, today]);
  const rows = useMemo(() => filterSessions(sessions, today, { status, from, to }), [sessions, today, status, from, to]);

  const input = 'px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-[11px] font-bold dark:text-white outline-none';
  const cards: { label: string; value: string; tone: string }[] = [
    { label: 'Realizadas', value: String(resumo.realizadas), tone: 'text-green-600' },
    { label: 'Previstas', value: String(resumo.previstas), tone: 'text-violet-600' },
    { label: 'Faltas', value: String(resumo.faltas), tone: 'text-rose-600' },
    { label: 'Cancelamentos', value: String(resumo.canceladas), tone: 'text-slate-500' },
    { label: 'Frequência', value: resumo.taxa === null ? '—' : `${Math.round(resumo.taxa * 100)}%`, tone: 'text-indigo-600' },
  ];

  return (
    <div className="bg-white dark:bg-slate-900 p-6 md:p-8 rounded-[2rem] sm:rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-sm space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b dark:border-slate-800 pb-4">
        <div>
          <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 uppercase tracking-wider flex items-center gap-2"><CalendarCheck size={20} className="text-indigo-500" /> Histórico de Frequência</h3>
          <p className="text-xs text-slate-400 dark:text-slate-500 font-bold mt-1">Sessões realizadas e previstas, conforme a agenda. Para alterar, use a Agenda.</p>
        </div>
        <button onClick={() => void load()} className="self-start px-5 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 dark:text-slate-200">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Atualizar
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">{c.label}</p>
            <p className={`text-2xl font-black mt-1 ${c.tone}`}>{c.value}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px] font-bold text-slate-500 dark:text-slate-400">
        <span>Primeira sessão realizada: <strong className="text-slate-700 dark:text-slate-200">{fmtDate(resumo.primeiraRealizada)}</strong></span>
        <span>Última sessão realizada: <strong className="text-slate-700 dark:text-slate-200">{fmtDate(resumo.ultimaRealizada)}</strong></span>
        <span>Próxima sessão prevista: <strong className="text-slate-700 dark:text-slate-200">{fmtDate(resumo.proximaPrevista)}</strong></span>
      </div>
      <p className="text-[10px] text-slate-400 font-medium -mt-3">Frequência = compareceu ÷ (compareceu + não compareceu). Cancelamentos e sessões futuras não entram no cálculo.</p>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1"><label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1" htmlFor="freq-de">De</label><input id="freq-de" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={`block ${input}`} /></div>
        <div className="space-y-1"><label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1" htmlFor="freq-ate">Até</label><input id="freq-ate" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={`block ${input}`} /></div>
        <div className="space-y-1"><label className="text-[9px] font-black uppercase tracking-widest text-slate-400 ml-1" htmlFor="freq-status">Status</label>
          <select id="freq-status" value={status} onChange={(e) => setStatus(e.target.value as FrequencyFilter)} className={`block ${input}`}>
            <option value="">Todas</option>
            <option value="previstas">Previstas</option>
            <option value="realizadas">Realizadas</option>
            <option value="faltas">Faltas</option>
            <option value="canceladas">Canceladas</option>
          </select>
        </div>
        {(status || from || to) && (
          <button onClick={() => { setStatus(''); setFrom(''); setTo(''); }} className="px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">Limpar filtros</button>
        )}
      </div>

      {error && <div role="alert" className="px-5 py-4 rounded-2xl text-xs font-bold bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300">Não foi possível carregar o histórico: {error}</div>}

      {loading ? (
        <p className="py-8 text-[11px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Carregando...</p>
      ) : rows.length === 0 ? (
        <p className="py-8 text-sm text-slate-400 italic text-center">{sessions.length ? 'Nenhuma sessão para os filtros escolhidos.' : 'Nenhuma sessão na agenda para este paciente.'}</p>
      ) : (
        <ul className="space-y-2 max-h-[60vh] overflow-y-auto custom-scrollbar pr-1">
          {rows.map((s) => {
            const st = FREQUENCY_STATUS[sessionStatus(s, today)];
            const [y, m, d] = s.date.split('-');
            const valor = fmtMoney(s.serviceValue);
            return (
              <li key={`${s.source}-${s.id}`} className="flex items-center gap-4 p-3 rounded-2xl border border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                <div className="w-14 shrink-0 text-center rounded-xl bg-slate-100 dark:bg-slate-800 py-1.5">
                  <p className="text-lg font-black text-slate-800 dark:text-white leading-none">{d}</p>
                  <p className="text-[9px] font-black text-slate-500 mt-0.5">{MESES[Number(m) - 1]}.{y.slice(2)}</p>
                </div>
                <div className="flex-1 min-w-0 space-y-1">
                  <p className="text-sm font-black text-slate-800 dark:text-slate-100">
                    {s.start} – {s.end}
                    {s.serviceName && <span className="ml-2 text-xs font-bold text-slate-500">{s.serviceName}</span>}
                    {s.recurrence && <Repeat size={12} className="inline ml-2 text-slate-400" aria-label="Recorrência" />}
                  </p>
                  <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span className="inline-flex items-center gap-1">{isOnline(s.modality) ? <Video size={12} /> : <MapPin size={12} />}{isOnline(s.modality) ? 'Online' : 'Presencial'}{s.locationName ? ` · ${s.locationName}` : ''}</span>
                    {s.professionalName && <span className="inline-flex items-center gap-1"><UserIcon size={12} />{s.professionalName}</span>}
                    {s.source === 'portal' && <span>Link externo</span>}
                  </p>
                </div>
                {valor && <p className="hidden sm:block text-sm font-black text-slate-700 dark:text-slate-200 whitespace-nowrap">{valor}</p>}
                <span className={`shrink-0 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest ${st.tone}`}>{st.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default PatientFrequency;
